'use client';
import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';

import { CacheKey } from '@eliadmin/core/src/api/constants';
import { fetchBanners } from '@eliadmin/core/src/api/general/general-api';
import type { Banner } from '@eliadmin/core/src/api/general/types/general-types';
import type { CarouselProps } from '@eliadmin/core/src/components/ui/carousel';
import { Carousel } from '@eliadmin/core/src/components/ui/carousel';
import type { AvailableLocales } from '@eliadmin/core/src/stores/locale-store';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { useSuspenseQuery } from '@tanstack/react-query';
import Image, { getImageProps } from 'next/image';
import { AiGeneratedBadge } from '@eliadmin/core/src/components/image/ai-generated-badge';
import { useState } from 'react';
import { getHeroImageLoader, isSameOriginHeroSource } from './hero-image-loader';

// All four Modra mobile creatives use this portrait ratio. Reserve their real
// space before decoding, and use a lighter mobile rendition (desktop unchanged).
const MOBILE_BANNER_HEIGHT = 599;
const MOBILE_BANNER_QUALITY = 60;
const EMPTY_BANNER =
  `data:image/svg+xml,${encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" width="768" height="599"/>')}`;
const EMPTY_DESKTOP_BANNER =
  `data:image/svg+xml,${encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" width="1920" height="430"/>')}`;

function hasAiBadge(url: string) {
  try {
    return new URL(url, 'https://placeholder.invalid').searchParams.get('ai') === '1';
  } catch {
    return false;
  }
}

export type BannerSliderProps = {
  banners?: Banner[];
  locale?: AvailableLocales;
  imageSizes?: {
    desktop?: string;
    mobile?: string;
  };
  className?: {
    main?: string;
    banner?: string;
  };

  carouselPropsMobile?: Omit<CarouselProps, 'slides'>;
  carouselPropsDesktop?: Omit<CarouselProps, 'slides'>;
};

const defaultDesktopProps = (data: Banner[]): Omit<CarouselProps, 'slides'> => ({
  options: {
    align: 'start',
    containScroll: 'trimSnaps',
  },
  className: {
    wrapper: 'w-full gap-0',
    main: 'w-full',
    container: 'h-auto w-full',
    slide:
      'relative h-auto overflow-hidden flex max-h-[430px] items-center justify-center basis-full',
    dots: 'visible md:hidden',
    arrows: 'visible md:hidden',
    customDots: 'hidden h-16 md:flex items-stretch',
    customDot:
      'flex h-full justify-center flex-1 items-center border-r border-primary/20 px-1 text-center text-sm font-semibold last:border-r-0',
  },
  customDots: {
    showCustomDots: true,
    customDots: data.filter(i => i.types?.includes('desktop') && i.image).map(item => item.name),
    colors: {
      activeColor: 'bg-accent text-accent-foreground',
      inactiveColor: 'bg-secondary',
    },
  },
});

const defaultMobileProps = (_data: Banner[]): Omit<CarouselProps, 'slides'> => ({
  options: {
    align: 'start',
    containScroll: 'trimSnaps',
  },
  showDots: true,
  className: {
    wrapper: 'h-max w-full gap-0',
    main: 'w-full',
    container: 'w-full',
    slide: 'relative h-auto flex items-center justify-center overflow-hidden basis-full',
    dots: 'visible md:hidden',
    arrows: 'visible md:hidden',
    customDots: 'hidden h-16 md:flex ',
    customDot:
      'flex h-full flex-1 items-center border-r border-primary/20 px-1 text-center text-sm font-semibold last:border-r-0',
  },
});

export function BannerSlider(props: BannerSliderProps) {
  return props.banners !== undefined ? (
    <BannerSliderContent {...props} banners={props.banners} />
  ) : (
    <FetchedBannerSlider {...props} />
  );
}

function FetchedBannerSlider(props: BannerSliderProps) {
  const storeLocale = useLocaleStore(s => s.currentLocale);
  const locale = props.locale ?? storeLocale;

  const { data: fetchedBanners } = useSuspenseQuery({
    queryKey: [CacheKey.BANNERS, 'slider', locale],
    queryFn: () => fetchBanners({ locale, query: { where: { type: { equals: 'slider' } } } }),
  });

  return <BannerSliderContent {...props} banners={fetchedBanners?.data ?? []} />;
}

function FirstBannerPreload({ banner, viewport, sizes, failedSources }: {
  banner?: Banner;
  viewport: 'desktop' | 'mobile';
  sizes: string;
  failedSources: Readonly<Record<string, true>>;
}) {
  if (!banner?.image?.url) return null;
  // Match Next Image's configured loader/candidates exactly. React does not
  // automatically preload images inside picture, and only one viewport may load.
  const { props: image } = getImageProps({
    src: banner.image.url,
    loader: getHeroImageLoader(banner.image.url, failedSources[banner.image.url] === true),
    alt: resolveImageAlt(banner.image, banner.name),
    width: viewport === 'desktop' ? 1920 : 768,
    height: viewport === 'desktop' ? 430 : MOBILE_BANNER_HEIGHT,
    quality: viewport === 'desktop' ? 75 : MOBILE_BANNER_QUALITY,
    sizes,
  });

  return (
    <link
      rel="preload"
      as="image"
      // React requires href to hoist this media-qualified link into the head.
      // Legacy browsers use the same fallback URL as the corresponding img.
      href={image.src}
      imageSrcSet={image.srcSet}
      imageSizes={image.sizes}
      media={viewport === 'desktop' ? '(min-width: 768px)' : '(max-width: 767px)'}
      fetchPriority="high"
    />
  );
}

function BannerSliderContent(props: BannerSliderProps & { banners: Banner[] }) {
  const { banners } = props;
  const [firstImageReady, setFirstImageReady] = useState(false);
  const [failedHeroSources, setFailedHeroSources] = useState<Readonly<Record<string, true>>>({});
  const releaseImages = () => setFirstImageReady(true);
  const onFirstError = (src: string) => {
    // A failed optimizer request retries this original source through Bunny
    // once. Repeated errors neither loop nor leave the other slides gated.
    if (isSameOriginHeroSource(src)) {
      setFailedHeroSources(failed => failed[src] ? failed : { ...failed, [src]: true });
    }
    releaseImages();
  };
  const onFirstLoad: React.ReactEventHandler<HTMLImageElement> = event => {
    // The hidden viewport's transparent source must not release the real slides.
    if (!event.currentTarget.currentSrc.startsWith('data:')) releaseImages();
  };

  const desktopProps = defaultDesktopProps(banners);
  const mobileProps = defaultMobileProps(banners);

  const desktopSlides = banners
    .filter(i => i.types?.includes('desktop') && i.image)
    .map((item, i) => (
      <a key={i} href={item.url || '#'} className="relative block h-full w-full">
        <picture>
          <source media="(max-width: 767px)" srcSet={EMPTY_BANNER} />
          {i > 0 && !firstImageReady && <source srcSet={EMPTY_DESKTOP_BANNER} />}
          <Image
            src={item.image.url}
            loader={i === 0 ? getHeroImageLoader(item.image.url, failedHeroSources[item.image.url] === true) : undefined}
            alt={resolveImageAlt(item?.image, item?.name)}
            width={1920}
            height={430}
            quality={75}
            loading={i === 0 ? 'eager' : 'lazy'}
            fetchPriority={i === 0 ? 'high' : 'low'}
            onLoad={i === 0 ? onFirstLoad : undefined}
            onError={i === 0 ? () => onFirstError(item.image.url) : undefined}
            sizes={props.imageSizes?.desktop ?? '100vw'}
            style={{ height: 'auto', width: '100%' }}
            className={cn(
              'block min-h-[300px] w-full object-cover object-center',
              'ea-banner-slider__banner',
              'ea-banner-slider__banner--desktop',
              props.className?.banner,
            )}
          />
        </picture>
        {hasAiBadge(item.image.url) && <AiGeneratedBadge />}
      </a>
    ));

  const mobileSlides = banners
    .filter(i => i.types?.includes('mobile') && i.image)
    .map((item, i) => (
      <a key={i} href={item.url || '#'} className="relative block h-full w-full">
        <picture>
          <source media="(min-width: 768px)" srcSet={EMPTY_BANNER} />
          {i > 0 && !firstImageReady && <source srcSet={EMPTY_BANNER} />}
          <Image
            src={item.image.url}
            loader={i === 0 ? getHeroImageLoader(item.image.url, failedHeroSources[item.image.url] === true) : undefined}
            alt={resolveImageAlt(item?.image, item?.name)}
            width={768}
            height={MOBILE_BANNER_HEIGHT}
            quality={MOBILE_BANNER_QUALITY}
            loading={i === 0 ? 'eager' : 'lazy'}
            fetchPriority={i === 0 ? 'high' : 'low'}
            onLoad={i === 0 ? onFirstLoad : undefined}
            onError={i === 0 ? () => onFirstError(item.image.url) : undefined}
            sizes={props.imageSizes?.mobile ?? '100vw'}
            style={{ height: 'auto', width: '100%' }}
            className={cn(
              'block min-h-[300px] w-full object-cover object-center',
              'ea-banner-slider__banner',
              'ea-banner-slider__banner--mobile',
              props.className?.banner,
            )}
          />
        </picture>
        {hasAiBadge(item.image.url) && <AiGeneratedBadge />}
      </a>
    ));

  if (!desktopSlides.length && !mobileSlides.length) return null;

  return (
    <>
      <FirstBannerPreload
        banner={banners.find(item => item.types?.includes('desktop') && item.image)}
        viewport="desktop"
        sizes={props.imageSizes?.desktop ?? '100vw'}
        failedSources={failedHeroSources}
      />
      <FirstBannerPreload
        banner={banners.find(item => item.types?.includes('mobile') && item.image)}
        viewport="mobile"
        sizes={props.imageSizes?.mobile ?? '100vw'}
        failedSources={failedHeroSources}
      />
      <div
        onPointerDownCapture={releaseImages}
        onFocusCapture={releaseImages}
        className={cn(
          'hidden w-full md:flex',
          'ea-banner-slider',
          'ea-banner-slider__desktop',
          props.className?.main,
        )}
      >
        <Carousel
          {...props.carouselPropsDesktop}
          slides={desktopSlides}
          deferUntilImagePaint
          {...desktopProps}
          enableLoop
          enableInterval
          intervalLength={7000}
        />
      </div>
      <div
        onPointerDownCapture={releaseImages}
        onFocusCapture={releaseImages}
        className={cn(
          'flex w-full md:hidden',
          'ea-banner-slider',
          'ea-banner-slider__mobile',
          props.className?.main,
        )}
      >
        <Carousel
          {...props.carouselPropsMobile}
          slides={mobileSlides}
          deferUntilImagePaint
          {...mobileProps}
          enableLoop
          enableInterval
          intervalLength={7000}
        />
      </div>
    </>
  );
}
